import type { Level } from '../engine/level';
import { isAdmin, isUnlocked, VISIBLE_MAX, type Progress } from './progress';
import { fitRotation, watchViewport } from './orient';
import { clientToLocal } from './input';

/** 아직 구현 안 된 원작 기믹이 있는 판 — 목록에서 주황으로 표시. (지금은 없음; 새 장치를 발견하면 여기에 플래그 이름을 넣는다) */
const UNSUPPORTED = new Set<string>([]);
function hasUnsupported(l: Level): boolean {
  return (l.src?.flags ?? []).some((f) => UNSUPPORTED.has(f));
}

export function renderSelect(host: HTMLElement, levels: Level[], progress: Progress, onPick: (id: number) => void): { destroy(): void } {
  const root = document.createElement('div');
  root.className = 'select';
  const cleared = new Set(progress.cleared);
  const admin = isAdmin();
  const shown = admin ? levels : levels.filter((l) => l.id <= VISIBLE_MAX);
  const maxCleared = progress.cleared.length ? progress.cleared[progress.cleared.length - 1]! : 0;
  // 이어하기 = 마지막 판이 열려 있으면 그것, 아니면 다음에 열 판
  const resume = isUnlocked(progress, progress.last) ? progress.last : Math.min(maxCleared + 1, shown.length ? shown[shown.length - 1]!.id : 1);
  root.innerHTML = `<div class="inner">
    <header>
      <h1>dots-draw</h1>
      <button class="btn resume">이어하기 · LEVEL ${String(resume).padStart(3, '0')}</button>
      <div class="sub">${[...cleared].filter((id) => admin || id <= VISIBLE_MAX).length} / ${shown.length}${admin ? ' · 관리자' : ''}</div>
    </header>
    <div class="grid"></div></div>`;
  const grid = root.querySelector('.grid')!;
  for (const l of shown) {
    const b = document.createElement('button');
    const locked = !isUnlocked(progress, l.id);
    b.className = 'cell' + (cleared.has(l.id) ? ' done' : '') + (l.id === progress.last ? ' last' : '') + (hasUnsupported(l) ? ' flag' : '') + (locked ? ' locked' : '');
    b.textContent = String(l.id);
    b.disabled = locked;
    if (!locked) b.addEventListener('click', () => onPick(l.id));
    grid.appendChild(b);
  }
  root.querySelector('.resume')!.addEventListener('click', () => onPick(resume));
  host.replaceChildren(root);

  // 세로 폰이면 90° 돌려 가로로. 돌린 요소는 브라우저 스크롤이 축과 어긋나므로 드래그로 직접 스크롤한다.
  const inner = root.querySelector<HTMLElement>('.inner')!;
  let offset = 0, dragging = false, startY = 0, startOffset = 0, moved = false;
  const clamp = () => { const max = Math.max(0, inner.scrollHeight - root.clientHeight); offset = Math.min(max, Math.max(0, offset)); inner.style.transform = `translateY(${-offset}px)`; };
  const fit = () => { fitRotation(root); clamp(); };
  const unwatch = watchViewport(fit);
  root.addEventListener('pointerdown', (e) => { dragging = true; moved = false; startY = clientToLocal(root, e.clientX, e.clientY)[1]; startOffset = offset; });
  root.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const y = clientToLocal(root, e.clientX, e.clientY)[1];
    if (Math.abs(y - startY) > 6) moved = true;
    offset = startOffset - (y - startY); clamp();
  });
  const up = () => { dragging = false; };
  root.addEventListener('pointerup', up); root.addEventListener('pointercancel', up);
  root.addEventListener('click', (e) => { if (moved) { e.stopPropagation(); e.preventDefault(); } }, true);
  root.addEventListener('wheel', (e) => { offset += e.deltaY; clamp(); }, { passive: true });
  return { destroy() { unwatch(); root.remove(); } };
}
